import { Link } from 'react-router-dom';
import SEO from '@/components/SEO';
import '@/styles/article.css';
import '@/styles/landing.css';

// A fragment, not a wrapper. article.css binds the measure and the paragraph
// rhythm with CHILD selectors (`.uxa-article > p`), so the h2 and the prose have
// to be direct children of the article. Wrapping them in two divs is what kept
// these pages at 14px while the blog moved to 19.
const Section = ({ title, children }) => (
  <>
    <h2>{title}</h2>
    {children}
  </>
);

export default function PrivacyPage() {
  return (
    <div className="min-h-screen pt-20">
      <SEO
        title="Privacy Policy"
        description="Learn how UXAuditPro collects, uses, and protects your personal data. We are committed to transparency and user privacy."
        canonical="/privacy"
        noIndex={false}
      />
      {/* THE MEASURE STAYS AT max-w-3xl. These are article pages; article.css
          binds --article-measure to 68ch and the module was verified conformant
          on 2026-08-19. The brand shell goes around the column, never through
          it. The grid wash sits behind the document header only. */}
      <div className="max-w-3xl mx-auto px-6 py-16" style={{ position: 'relative' }}>
        {/* Header */}
        <div className="mb-12" style={{ position: 'relative' }}>
          <div className="uxa-hero-grid" aria-hidden="true" /* inset was '-64px -40px 0' and produced 16px of horizontal
                   overflow at 390 and 768 — the column is px-6 (24px), so a
                   -40px bleed has nowhere to go. Measured on the built page:
                   scrollWidth 406 against a 390 client. A decorative wash is
                   never a reason for the page to scroll sideways. */
                style={{ inset: '-64px 0 0' }} />
          <div style={{ position: 'relative' }}>
          <span className="text-sm font-bold uppercase tracking-widest" style={{ color: 'var(--accent-text)' }}>Legal</span>
          <h1 className="uxa-h1 mt-3" style={{ color: 'var(--ink-1)', textWrap: 'pretty' }}>
            Privacy Policy
          </h1>
          <p className="uxa-meta mt-3">Last updated: October 2026</p>
          </div>
        </div>

        <div className="uxa-article uxa-feature-card" style={{ background: 'var(--surface-white)', padding: 'var(--space-10)' }}>
          <Section title="1. Information We Collect">
            <p>We collect information you provide directly to us when you create an account, submit a UX audit, or contact our support team. This includes your name, email address, and any files or URLs you upload for analysis.</p>
            <p>We automatically collect certain usage information when you interact with our services, including log data, device information, IP address, browser type, and pages visited.</p>
          </Section>

          <Section title="2. How We Use Your Information">
            <p>We use the information we collect to provide, maintain, and improve our services — including processing your UX audits, generating reports, and delivering results to you.</p>
            <p>We may also use your information to send transactional emails (e.g. audit completion notifications), respond to support requests, and prevent fraud or abuse.</p>
            <p>We do not sell your personal data to third parties.</p>
          </Section>

          <Section title="3. Data Storage and Security">
            <p>Your data is stored on secure servers hosted on industry-standard cloud infrastructure. We implement appropriate technical and organizational measures to protect your information against unauthorized access, loss, or disclosure.</p>
            <p>Uploaded screenshots and audit files are stored securely and are only accessible to you and our processing systems. See our <Link to="/security/">Security page</Link> for more details.</p>
          </Section>

          <Section title="4. Data Retention">
            <p>We retain your account data for as long as your account is active. Audit reports and uploaded files are retained for 12 months after creation, after which they may be deleted.</p>
            <p>You may request deletion of your data at any time by contacting us at <a href="mailto:privacy@uxauditpro.com">privacy@uxauditpro.com</a>.</p>
          </Section>

          <Section title="5. Cookies">
            <p>We use cookies and similar tracking technologies to maintain your session, remember your preferences, and understand how you use our service. You can control cookies through your browser settings.</p>
          </Section>

          <Section title="6. Third-Party Services">
            <p>We use select third-party services to operate our platform: PayPal and Razorpay for payments; Google for sign-in; Anthropic and OpenAI to analyse the pages you submit and write your report; Amazon Web Services to send email; and Cloudflare to store screenshots and reports. When you audit a Figma design, we read it from Figma. Each provider has its own privacy policy.</p>
          </Section>

          <Section title="7. Your Rights">
            <p>Depending on your location, you may have rights to access, correct, or delete your personal data, object to certain processing, or request data portability. To exercise these rights, contact us at <a href="mailto:privacy@uxauditpro.com">privacy@uxauditpro.com</a>.</p>
          </Section>

          <Section title="8. Changes to This Policy">
            <p>We may update this Privacy Policy from time to time. We will notify you of significant changes by email or through a prominent notice in the application. Continued use of our services after changes constitutes acceptance of the updated policy.</p>
          </Section>

          <Section title="9. Contact Us">
            <p>If you have questions about this Privacy Policy, please contact us at <a href="mailto:privacy@uxauditpro.com">privacy@uxauditpro.com</a>.</p>
          </Section>

          <Section title="10. Connected AI Assistants">
            <p>If you connect UXAuditPro to an AI assistant such as Claude or ChatGPT, the assistant receives what our tools return when it uses them: your audit list, scores, findings and comparisons, and your account email, plan and remaining audits. Screenshots are never sent. Nothing is sent unless the assistant calls one of our tools. You can disconnect at any time from Connected apps on your Account page, and its access ends straight away. How the assistant's provider handles your conversations is covered by that provider's policies.</p>
          </Section>

          <Section title="11. Children's Privacy">
            <p>UXAuditPro is not intended for anyone under 18, and we do not knowingly collect their data.</p>
          </Section>
        </div>

        {/* 44px MINIMUM. These measured 88x20, 53x20 and 34x20 on the built
            page at all four widths. They are a standalone document-switcher
            row, not links inside a sentence, so WCAG 2.5.8's inline-prose
            exemption does not reach them. Presented as a 44px hit area via
            inline-flex so the row does not move. */}
        <div className="mt-8 flex gap-4 text-sm items-center" style={{ color: 'var(--ink-2)' }}>
          <Link to="/terms/" style={{ color: 'var(--accent-text)', display: 'inline-flex', alignItems: 'center', minHeight: 44 }}>Terms of Service</Link>
          <span>·</span>
          <Link to="/security/" style={{ color: 'var(--accent-text)', display: 'inline-flex', alignItems: 'center', minHeight: 44 }}>Security</Link>
          <span>·</span>
          <Link to="/legal/" style={{ color: 'var(--accent-text)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minHeight: 44, minWidth: 44 }}>Legal</Link>
        </div>
      </div>
    </div>
  );
}
